import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { StatCard } from "../components/StatCard";
import { DOCTOR_STAGES, CLINICIAN_STAGES, PATIENT_STAGES, LEAD_STAGES, inr } from "../lib/constants";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Stethoscope, HeartPulse, Users, Filter, TrendingUp, Pencil, Check, Plus, X, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, Area, XAxis, YAxis, Tooltip, CartesianGrid, Line, ComposedChart } from "recharts";

const WIDGETS = [
  { id: "kpis", label: "KPI Cards" },
  { id: "revenue", label: "Revenue Rollup" },
  { id: "ramp", label: "Doctor Ramp Chart" },
  { id: "pipeline", label: "Sales Pipeline" },
  { id: "doctor_funnel", label: "Doctor Acquisition" },
  { id: "clinician_funnel", label: "Clinician Sourcing" },
  { id: "patient_funnel", label: "Patient Onboarding" },
];
const ALL_IDS = WIDGETS.map((w) => w.id);

const FunnelSnapshot = ({ title, funnel, stages, color, onClick, testid }) => {
  const max = Math.max(...Object.values(funnel.counts), 1);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 h-full" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold font-head text-ccc-navy">{title}</h3>
        <button onClick={onClick} className="text-xs font-medium text-ccc-blue hover:underline">View funnel →</button>
      </div>
      <div className="space-y-2">
        {stages.map((s) => {
          const c = funnel.counts[s.key] || 0;
          return (
            <div key={s.key} className="flex items-center gap-2">
              <span className="text-xs text-slate-500 w-32 truncate">{s.label}</span>
              <div className="flex-1 h-5 bg-slate-100 rounded-md overflow-hidden">
                <div className="h-full rounded-md flex items-center justify-end pr-2" style={{ width: `${(c / max) * 100}%`, background: color, minWidth: c ? 24 : 0 }}>
                  {c > 0 && <span className="text-[10px] font-semibold text-white">{c}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between text-xs">
        <span className="text-slate-500">Total: <b className="text-ccc-navy">{funnel.total}</b></span>
        <span className="text-slate-500">Conversion: <b className="text-ccc-emerald">{funnel.conversion_pct}%</b></span>
      </div>
    </div>
  );
};

const Widget = ({ id, enabled, edit, onRemove, children, className }) => {
  if (!enabled.has(id)) return null;
  return (
    <div className={`relative ${className || ""}`} data-testid={`widget-${id}`}>
      {edit && (
        <>
          <div className="absolute inset-0 rounded-2xl ring-2 ring-dashed ring-ccc-blue/40 pointer-events-none z-10" />
          <button data-testid={`remove-widget-${id}`} onClick={() => onRemove(id)} className="absolute -top-2.5 -right-2.5 z-20 h-6 w-6 rounded-full bg-ccc-rose text-white flex items-center justify-center shadow hover:bg-rose-600 transition-colors">
            <X className="h-3.5 w-3.5" />
          </button>
        </>
      )}
      {children}
    </div>
  );
};

export default function Dashboard() {
  const [ov, setOv] = useState(null);
  const [ramp, setRamp] = useState(null);
  const [enabled, setEnabled] = useState(() => new Set(ALL_IDS));
  const [edit, setEdit] = useState(false);
  const [overdue, setOverdue] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/patients/overdue").then((r) => setOverdue(r.data));
    api.get("/reports/overview").then((r) => setOv(r.data));
    api.get("/reports/doctor-ramp").then((r) => setRamp(r.data));
    api.get("/settings/dashboard").then((r) => {
      if (Array.isArray(r.data.layout)) setEnabled(new Set(r.data.layout));
    });
  }, []);

  const persist = (set) => {
    const layout = WIDGETS.filter((w) => set.has(w.id)).map((w) => w.id);
    api.put("/settings/dashboard", { layout });
  };
  const removeWidget = (id) => { const s = new Set(enabled); s.delete(id); setEnabled(s); persist(s); };
  const addWidget = (id) => { const s = new Set(enabled); s.add(id); setEnabled(s); persist(s); };
  const hiddenWidgets = WIDGETS.filter((w) => !enabled.has(w.id));

  if (!ov) return <div className="text-slate-400">Loading dashboard…</div>;
  const k = ov.kpis;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-head text-ccc-navy" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Acquisition across all three funnels — pan-India.</p>
        </div>
        <div className="flex items-center gap-2">
          {edit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="add-widget-button" disabled={hiddenWidgets.length === 0}>
                  <Plus className="h-4 w-4 mr-1.5" /> Add widget
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Add a widget</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {hiddenWidgets.length === 0 && <DropdownMenuItem disabled>All widgets shown</DropdownMenuItem>}
                {hiddenWidgets.map((w) => (
                  <DropdownMenuItem key={w.id} data-testid={`add-widget-${w.id}`} onClick={() => addWidget(w.id)}>{w.label}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button data-testid="edit-dashboard-button" variant={edit ? "default" : "outline"} onClick={() => setEdit((e) => !e)} className={edit ? "bg-ccc-blue hover:bg-ccc-blue2 text-white" : ""}>
            {edit ? <><Check className="h-4 w-4 mr-1.5" /> Done</> : <><Pencil className="h-4 w-4 mr-1.5" /> Edit dashboard</>}
          </Button>
        </div>
      </div>

      {overdue?.count > 0 && (
        <button onClick={() => navigate("/patients?overdue=1")} data-testid="dashboard-overdue-alert" className="w-full text-left flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 hover:bg-amber-100 transition-colors">
          <AlertTriangle className="h-4 w-4" /><b>{overdue.count}</b> enrolled patient{overdue.count > 1 ? "s" : ""} unpaid for {overdue.days_threshold}+ days · {inr(overdue.total)} overdue — review in Patients
        </button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 auto-rows-min grid-flow-row-dense" data-testid="dashboard-grid">
        <Widget id="kpis" enabled={enabled} edit={edit} onRemove={removeWidget} className="lg:col-span-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testid="kpi-active-doctors" label="Active Doctors" value={`${k.active_doctors}`} sub={`Target ${k.doctor_target} by M12`} icon={Stethoscope} accent="#D97706" />
            <StatCard testid="kpi-active-clinicians" label="Active Clinicians" value={k.active_clinicians} sub="Accepting cases" icon={HeartPulse} accent="#0D9488" />
            <StatCard testid="kpi-patients" label="Patients" value={k.total_patients} sub={`${k.in_treatment} in treatment`} icon={Users} accent="#2540FF" />
            <StatCard testid="kpi-open-leads" label="Open Leads" value={k.open_leads} sub={`${k.converted_leads} converted`} icon={Filter} accent="#10B981" />
          </div>
        </Widget>

        <Widget id="revenue" enabled={enabled} edit={edit} onRemove={removeWidget} className="lg:col-span-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-x-10 gap-y-3" data-testid="revenue-rollup">
            <div><p className="text-xs text-slate-500">Booked revenue (all patients)</p><p className="text-2xl font-bold font-head text-ccc-blue mt-0.5">{inr(k.expected_revenue || 0)}</p></div>
            <div><p className="text-xs text-slate-500">Collected</p><p className="text-xl font-bold font-head text-ccc-emerald mt-0.5">{inr(k.paid_revenue || 0)}</p></div>
            <div><p className="text-xs text-slate-500">Outstanding</p><p className="text-xl font-bold font-head text-ccc-amber mt-0.5">{inr((k.expected_revenue || 0) - (k.paid_revenue || 0))}</p></div>
          </div>
        </Widget>

        <Widget id="ramp" enabled={enabled} edit={edit} onRemove={removeWidget} className="lg:col-span-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 h-full" data-testid="doctor-ramp-chart">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold font-head text-ccc-navy flex items-center gap-2"><TrendingUp className="h-4 w-4 text-ccc-amber" /> Doctor Ramp — {ramp?.series?.[0]?.target ?? 40} → {ramp?.target ?? 160}</h3>
                <p className="text-xs text-slate-500 mt-0.5">Active doctors vs Month-12 target</p>
              </div>
              <span className="text-2xl font-bold font-head text-ccc-navy">{ramp?.current_active ?? "—"}<span className="text-sm text-slate-400 font-normal"> / {ramp?.target ?? 160}</span></span>
            </div>
            {ramp && (
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={ramp.series}>
                  <defs>
                    <linearGradient id="actualFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2540FF" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#2540FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="actual" stroke="#2540FF" strokeWidth={2.5} fill="url(#actualFill)" name="Actual active" />
                  <Line type="monotone" dataKey="target" stroke="#D97706" strokeWidth={2} strokeDasharray="6 4" dot={false} name="Target ramp" />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </Widget>

        <Widget id="pipeline" enabled={enabled} edit={edit} onRemove={removeWidget} className="lg:col-span-2">
          <FunnelSnapshot testid="snapshot-leads" title="Sales Pipeline" funnel={ov.lead_funnel} stages={LEAD_STAGES} color="#10B981" onClick={() => navigate("/leads")} />
        </Widget>

        <Widget id="doctor_funnel" enabled={enabled} edit={edit} onRemove={removeWidget} className="lg:col-span-2">
          <FunnelSnapshot testid="snapshot-doctors" title="Doctor Acquisition" funnel={ov.doctor_funnel} stages={DOCTOR_STAGES} color="#D97706" onClick={() => navigate("/doctors")} />
        </Widget>
        <Widget id="clinician_funnel" enabled={enabled} edit={edit} onRemove={removeWidget} className="lg:col-span-2">
          <FunnelSnapshot testid="snapshot-clinicians" title="Clinician Sourcing" funnel={ov.clinician_funnel} stages={CLINICIAN_STAGES} color="#0D9488" onClick={() => navigate("/clinicians")} />
        </Widget>
        <Widget id="patient_funnel" enabled={enabled} edit={edit} onRemove={removeWidget} className="lg:col-span-2">
          <FunnelSnapshot testid="snapshot-patients" title="Patient Onboarding" funnel={ov.patient_funnel} stages={PATIENT_STAGES} color="#2540FF" onClick={() => navigate("/patients")} />
        </Widget>
      </div>

      {enabled.size === 0 && (
        <div className="text-center py-16 text-slate-400 border border-dashed border-slate-200 rounded-2xl" data-testid="dashboard-empty">
          No widgets. Click <b>Edit dashboard</b> → <b>Add widget</b> to bring them back.
        </div>
      )}
    </div>
  );
}
